import { useEffect, useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ROLE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";

const W = 540;
const H = 860;

const loadImg = (src) => new Promise((res) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
const photoOf = async (u) => {
  if (!u.has_photo) return null;
  try { const r = await api.get(`/users/${u.id}/photo`, { responseType: "blob" }); return await loadImg(URL.createObjectURL(r.data)); } catch { return null; }
};

const wrap = (ctx, text, x, y, max, lh) => {
  const words = String(text).split(" ");
  let line = "";
  words.forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > max && line) { ctx.fillText(line, x, y); line = w; y += lh; } else line = t;
  });
  ctx.fillText(line, x, y);
  return y;
};

const draw = async (canvas, u, s, logoSrc, accent) => {
  const ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent; ctx.fillRect(0, 0, W, 250);
  ctx.beginPath(); ctx.ellipse(W / 2, 250, W * 0.75, 70, 0, 0, Math.PI); ctx.fill();
  const logo = logoSrc ? await loadImg(logoSrc) : null;
  if (logo) ctx.drawImage(logo, 36, 34, 64, 64);
  ctx.fillStyle = "#fff"; ctx.textAlign = logo ? "left" : "center";
  ctx.font = "bold 26px sans-serif"; ctx.fillText(s.app_name, logo ? 116 : W / 2, 66);
  ctx.font = "15px sans-serif"; ctx.fillText("KARTU IDENTITAS PEGAWAI", logo ? 116 : W / 2, 92);
  const ph = await photoOf(u);
  const cx = W / 2; const cy = 330; const r = 120;
  ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r + 8, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill();
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
  if (ph) { const m = Math.min(ph.width, ph.height); ctx.drawImage(ph, (ph.width - m) / 2, (ph.height - m) / 2, m, m, cx - r, cy - r, r * 2, r * 2); }
  else { ctx.fillStyle = "#e5e7eb"; ctx.fillRect(cx - r, cy - r, r * 2, r * 2); ctx.fillStyle = "#9ca3af"; ctx.font = "bold 90px sans-serif"; ctx.textAlign = "center"; ctx.fillText((u.name || "?")[0].toUpperCase(), cx, cy + 32); }
  ctx.restore();
  ctx.textAlign = "center"; ctx.fillStyle = "#111827"; ctx.font = "bold 32px sans-serif";
  const y = wrap(ctx, u.name, cx, 510, W - 60, 38);
  ctx.fillStyle = accent; ctx.font = "bold 20px sans-serif"; ctx.fillText(ROLE_LABELS[u.role] || u.role, cx, y + 38);
  ctx.fillStyle = "#4b5563"; ctx.font = "17px sans-serif";
  const office = u.cabang_name ? `Kantor Cabang ${u.cabang_name}` : u.wilayah_name ? `Kantor Wilayah ${u.wilayah_name}` : "Kantor Pusat";
  ctx.fillText(office, cx, y + 70);
  const rows = [["ID", u.id.slice(0, 8).toUpperCase()], ["Email", u.email], ["WhatsApp", u.phone || "-"]];
  ctx.textAlign = "left"; ctx.font = "16px sans-serif";
  rows.forEach(([k, v], i) => { const ry = y + 125 + i * 32; ctx.fillStyle = "#6b7280"; ctx.fillText(k, 60, ry); ctx.fillStyle = "#111827"; ctx.fillText(String(v), 170, ry); });
  ctx.fillStyle = accent; ctx.fillRect(0, H - 34, W, 34);
  ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.font = "13px sans-serif"; ctx.fillText(u.active === false ? "NONAKTIF" : "Berlaku selama masih bertugas", cx, H - 12);
};

export const IdCard = ({ u, className = "" }) => {
  const ref = useRef(null);
  const { settings, logoSrc } = useSettings();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const accent = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
    setReady(false);
    draw(ref.current, u, settings, logoSrc, accent ? `hsl(${accent})` : "#059669").then(() => setReady(true));
  }, [u, settings, logoSrc]);
  const file = () => new Promise((res) => ref.current.toBlob((b) => res(new File([b], `idcard-${u.name.replace(/\s+/g, "-")}.png`, { type: "image/png" })), "image/png"));
  const download = async () => { const f = await file(); const a = document.createElement("a"); a.href = URL.createObjectURL(f); a.download = f.name; a.click(); };
  const share = async () => {
    const f = await file();
    if (navigator.canShare && navigator.canShare({ files: [f] })) { try { await navigator.share({ files: [f], title: `ID Card ${u.name}` }); } catch { /* dibatalkan */ } }
    else { await download(); toast.info("Perangkat tidak mendukung bagikan, kartu diunduh"); }
  };
  return (
    <div className={`flex flex-col items-center gap-3 ${className}`} data-testid={`id-card-${u.id}`}>
      <canvas ref={ref} className="w-full max-w-[270px] rounded-2xl shadow-md border bg-white" style={{ aspectRatio: `${W}/${H}` }} />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full h-9" disabled={!ready} onClick={download} data-testid={`id-card-download-${u.id}`}><Download className="h-4 w-4 mr-1" />Unduh</Button>
        <Button size="sm" className="rounded-full h-9" disabled={!ready} onClick={share} data-testid={`id-card-share-${u.id}`}><Share2 className="h-4 w-4 mr-1" />Bagikan</Button>
      </div>
    </div>
  );
};
